import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setAIReview } from '../store/actions';
import { HybridStreamManager } from '../services/HybridStreamManager';
import { fetchAIReviewById } from '../api/aiReviewService';
import { AIReviewStatus } from '../common/models/types';
import { log, LogLevel } from '../utils/LogUtil';

/**
 * 스트림 끊김 상태와 재확인 수단 (spec 2026-08-31-phai-웹-LLM실패-사용자안내 · 화면 설계 "부분 실패" 칸).
 * 화면을 벗어나면 사라져도 무해하므로 스토어가 아니라 화면 로컬 상태로 둔다.
 */
export interface StreamDisconnection {
  /** 재연결이 소진돼 화면이 무표시로 남는 상태. */
  isDisconnected: boolean;
  /** [새로고침] 재조회가 진행 중(연타 억제). */
  isRechecking: boolean;
  /** 검토 상세를 한 번 다시 조회하고, 여전히 진행 중이면 스트림을 다시 연결한다. */
  onRecheck: () => void;
}

export const useAIReviewStream = (reviewId: number | null): StreamDisconnection => {
  const dispatch = useDispatch();
  const [isDisconnected, setIsDisconnected] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const review = useSelector((state: RootState) => 
    reviewId ? state.data.reviews[reviewId] : null
  );
  
  useEffect(() => {
    log(LogLevel.INFO, `[useAIReviewStream] Hook called with reviewId: ${reviewId}`);
    
    if (!reviewId) {
      log(LogLevel.INFO, `[useAIReviewStream] No reviewId provided, returning`);
      return;
    }
    
    const streamManager = HybridStreamManager.getInstance();
    
    // 리뷰 상태에 따른 처리 분기
    if (review) {
      // 완료/실패 상태이면서 상세 데이터가 없는 경우
      if ((review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED) && !review.reviewResult) {
        log(LogLevel.INFO, `[useAIReviewStream] Review ${reviewId} is ${review.status === AIReviewStatus.COMPLETED ? 'COMPLETED' : 'FAILED'} but missing data, fetching via API`);
        
        // API로 상세 데이터 가져오기
        fetchAIReviewById(reviewId)
          .then(response => {
            if (!response.error && response.data) {
              log(LogLevel.INFO, `[useAIReviewStream] Successfully fetched review data for ${reviewId}`);
              dispatch(setAIReview(response.data));
            } else {
              log(LogLevel.ERROR, `[useAIReviewStream] Failed to fetch review data: ${response.message}`);
            }
          })
          .catch(error => {
            log(LogLevel.ERROR, `[useAIReviewStream] Error fetching review ${reviewId}:`, error);
          });
        
        // 완료된 리뷰는 스트림 연결하지 않음
        return;
      }
      
      // 진행 중인 리뷰만 스트림 연결
      if (review.status >= AIReviewStatus.PENDING && review.status < AIReviewStatus.COMPLETED) {
        log(LogLevel.INFO, `[useAIReviewStream] Starting stream for in-progress review: ${reviewId}`);
        streamManager.startStream(reviewId);
      } else if (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED) {
        // 이미 데이터가 있는 완료/실패 리뷰는 아무것도 하지 않음
        log(LogLevel.INFO, `[useAIReviewStream] Review ${reviewId} already has data, skipping`);
      }
    } else {
      // 리뷰 정보가 없으면 스트림 시작 (초기 로드)
      log(LogLevel.INFO, `[useAIReviewStream] No review data in store, starting stream for reviewId: ${reviewId}`);
      streamManager.startStream(reviewId);
    }
    
    // cleanup: 진행 중인 stream은 완료까지 유지
    // 컴포넌트가 언마운트되어도 분석이 진행 중이면 stream 유지
    return () => {
      log(LogLevel.INFO, `[useAIReviewStream] Cleanup called for reviewId: ${reviewId} (stream not stopped)`);
      // stream 종료 로직 제거 - 서버에서 완료/실패 시 자동 종료됨
    };
  }, [reviewId, review?.status, review?.reviewResult, dispatch]);

  // 스트림 끊김 구독 — 재연결이 소진되면 화면이 무표시로 남지 않도록 끊김 상태로 바꾼다.
  useEffect(() => {
    if (!reviewId) return;

    return HybridStreamManager.getInstance().onStreamDisconnected(reviewId, () => {
      log(LogLevel.WARNING, `[useAIReviewStream] Stream disconnected for review ${reviewId}`);
      setIsDisconnected(true);
    });
  }, [reviewId]);

  const onRecheck = useCallback(async () => {
    if (!reviewId || isRechecking) return;

    setIsRechecking(true);
    try {
      const response = await fetchAIReviewById(reviewId);
      const fetched = response?.data;

      if (!response?.error && fetched) {
        dispatch(setAIReview(fetched));

        // 이미 끝난 검토면 보고서·실패 카드가 상태를 그대로 설명한다.
        if (fetched.status === AIReviewStatus.COMPLETED || fetched.status === AIReviewStatus.FAILED) {
          setIsDisconnected(false);
          return;
        }
      }

      // 여전히 진행 중 — 스트림을 다시 연결한다. 연결이 서지 않으면 끊김 상태를 유지해 버튼을 다시 쓰게 둔다.
      const streamManager = HybridStreamManager.getInstance();
      await streamManager.startHybridStream(reviewId);
      setIsDisconnected(!streamManager.getConnectionMode(reviewId));
    } catch (error) {
      log(LogLevel.ERROR, `[useAIReviewStream] Recheck failed for review ${reviewId}:`, error);
      // 끊김 상태 유지 — 버튼을 다시 누를 수 있다.
    } finally {
      setIsRechecking(false);
    }
  }, [reviewId, isRechecking, dispatch]);

  return { isDisconnected, isRechecking, onRecheck };
};